import type { FeatureOption } from './FeatureSelector'

import { Typography, CircularProgress, useTheme, Box } from '@mui/material'
import { motion } from 'framer-motion'
import { useState } from 'react'

export interface FeatureCardProps {
  feature: FeatureOption
  isHorizontal?: boolean
  selectedFeatureId?: string
  creatingSet?: boolean
  onClick: (feature: FeatureOption) => void
  selected?: boolean
  tag?: JSX.Element
}

export function FeatureCard({
  feature,
  isHorizontal = false,
  selectedFeatureId,
  creatingSet = false,
  onClick,
  selected = false,
  tag,
}: FeatureCardProps) {
  const theme = useTheme()
  const [isHovered, setIsHovered] = useState(false)

  return (
    <motion.div
      animate={{
        scale: !isHorizontal && isHovered ? 1.05 : 1,
        boxShadow: !isHorizontal && isHovered ? '0px 8px 25px rgba(0, 0, 0, 0.15)' : '0px 2px 8px rgba(0, 0, 0, 0.1)',
      }}
      style={{
        borderRadius: '16px',
        overflow: 'hidden',
        cursor: 'pointer',
        position: 'relative',
        width: isHorizontal ? '100%' : 156,
        height: isHorizontal ? 'auto' : 156,
        padding: '16px',
        background: feature.color,
        border: selected ? `2px solid ${theme.palette.primary.main}` : 'none',
        boxShadow: selected ? '0px 4px 12px rgba(0, 0, 0, 0.15)' : '0px 2px 8px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: isHorizontal ? 'row' : 'column',
        alignItems: isHorizontal ? 'center' : 'flex-start',
        justifyContent: isHorizontal ? 'flex-start' : 'flex-start',
        gap: '6px',
      }}
      transition={{
        type: 'spring',
        stiffness: 300,
        damping: 20,
      }}
      whileTap={{ scale: !isHorizontal ? 0.98 : 1 }}
      onClick={() => {
        onClick(feature)
      }}
      onHoverEnd={() => {
        if (!isHorizontal) setIsHovered(false)
      }}
      onHoverStart={() => {
        if (!isHorizontal) setIsHovered(true)
      }}
    >
      {/* Background Icon - Static */}
      <div
        style={{
          position: 'absolute',
          zIndex: 0,
          pointerEvents: 'none',
          ...(feature.id === 'BULK_CREATE' ? { top: 0, right: 0 } : { right: 0, bottom: 0 }),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {feature.bgIcon}
      </div>

      {/* Icon Container */}
      <motion.div
        animate={{
          scale: !isHorizontal && isHovered ? 1.1 : 1,
          rotate: !isHorizontal && isHovered ? 5 : 0,
        }}
        style={{
          position: 'relative',
          zIndex: 1,
          marginBottom: '6px',
        }}
        transition={{
          type: 'spring',
          stiffness: 400,
          damping: 10,
        }}
      >
        <div
          style={{
            width: 60,
            height: 60,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '14.4px',
            background: '#EFF4FF',
          }}
        >
          {selectedFeatureId === feature.id && creatingSet ? (
            <CircularProgress size={25} sx={{ color: feature.color }} />
          ) : (
            feature.icon
          )}
        </div>
      </motion.div>

      {/* Title */}
      <motion.div
        animate={{
          scale: !isHorizontal && isHovered ? 1.05 : 1,
          y: !isHorizontal && isHovered ? -2 : 0,
        }}
        style={{
          position: 'relative',
          zIndex: 1,
        }}
        transition={{
          type: 'spring',
          stiffness: 300,
          damping: 15,
        }}
      >
        <Typography color="#fff" fontSize={16} fontWeight={600} lineHeight="24px">
          {feature.title}
        </Typography>
      </motion.div>

      {/* Tag */}
      {tag ? (
        <motion.div
          animate={{
            scale: !isHorizontal && isHovered ? 1.15 : 1,
            rotate: !isHorizontal && isHovered ? -5 : 0,
          }}
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 1,
          }}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 10,
          }}
        >
          <Box fontSize={12}>{tag}</Box>
        </motion.div>
      ) : null}
    </motion.div>
  )
}
